import { useRef, useState, useEffect } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Stack,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Chip,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import { BASE_URL } from "../../../components/config/config";
import axios from "axios";
import {
  Edit,
  Delete,
  Visibility,
  Close,
  Publish,
  Save,
} from "@mui/icons-material";
import { ToggleOn, ToggleOff } from "@mui/icons-material";
import "react-quill/dist/quill.snow.css";
import { format } from "date-fns";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { useSelector } from "react-redux";
import {
  canViewBlog,
  canAddBlog,
  canUpdateBlog,
  canDeleteBlog,
  hasFullBlogAccess,
} from "../../../utility/permissionUtils";

const QuillEditor = ({ value, onChange, ...props }) => {
  const quillRef = useRef(null);
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) {
      const quill = new Quill(editorRef.current, {
        theme: "snow",
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            ["blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
          ],
        },
        formats: [
          "header",
          "bold",
          "italic",
          "underline",
          "strike",
          "blockquote",
          "list",
          "bullet",
          "link",
          "image",
        ],
      });

      quill.root.innerHTML = value || "";

      quill.on("text-change", () => {
        const content = quill.root.innerHTML;
        onChange(content);
      });

      quillRef.current = quill;
    }

    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (quillRef.current && quillRef.current.root.innerHTML !== value) {
      quillRef.current.root.innerHTML = value || "";
    }
  }, [value]);

  return <div ref={editorRef} style={{ height: "400px" }} {...props} />;
};

const CreateBlog = () => {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  // Check permissions
  const canView = canViewBlog(userPermissions);
  const canAdd = canAddBlog(userPermissions);
  const canUpdate = canUpdateBlog(userPermissions);
  const canDelete = canDeleteBlog(userPermissions);
  const hasFullAccess = hasFullBlogAccess(userPermissions);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    content: "",
    summary: "",
    tags: "",
    categories: "",
    author: "",
    publishDate: "",
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [postedBlog, setPostedBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [blogFilter, setBlogFilter] = useState("active");
  const [loadingToggles, setLoadingToggles] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        // 1MB limit
        alert("Image size should be less than 1MB");
        return;
      }
      setSelectedImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCardClick = (blog) => {
    setSelectedBlog(blog);
    setOpenDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check permission for add/update
    if (
      (!isEditing && !canAdd && !hasFullAccess) ||
      (isEditing && !canUpdate && !hasFullAccess)
    ) {
      alert("You don't have permission to perform this action");
      return;
    }

    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append("title", formData.title);
    formDataToSend.append("author", formData.author);
    formDataToSend.append("content", formData.content);
    formDataToSend.append("summary", formData.summary);
    formDataToSend.append("categories", formData.categories);
    if (selectedImageFile) {
      formDataToSend.append("blogimage", selectedImageFile);
    }

    try {
      let response;
      if (isEditing && editingBlogId) {
        // Edit API
        formDataToSend.append("blogId", editingBlogId);
        response = await axios.post(
          `${BASE_URL}blogs/updateBlogs`,
          formDataToSend,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      } else {
        // Create API
        response = await axios.post(
          `${BASE_URL}blogs/createBlogs`,
          formDataToSend,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
      }

      setPostedBlog(response.data);
      fetchAllBlogs();

      // Reset form and states
      setFormData({
        title: "",
        description: "",
        summary: "",
        content: "",
        tags: "",
        categories: "",
        author: "",
        publishDate: "",
      });
      setImagePreview(null);
      setSelectedImageFile(null);
      setIsEditing(false);
      setEditingBlogId(null);
    } catch (error) {
      console.error("Error submitting blog:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditClick = (blog) => {
    // Check update permission
    if (!canUpdate && !hasFullAccess) {
      alert("You don't have permission to edit blogs");
      return;
    }

    setFormData({
      title: blog.title || "",
      description: blog.description || "",
      summary: blog.summary || "",
      content: blog.content || "",
      tags: blog.tags || "",
      categories: blog.categories?.join(", ") || "",
      author: blog.author || "",
      publishDate: blog.publishDate || "",
    });
    setImagePreview(blog.image || null);
    setSelectedImageFile(null);
    setEditingBlogId(blog._id);
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fetchAllBlogs = async () => {
    // Check view permission
    if (!canView && !hasFullAccess) {
      setAllBlogs([]);
      return;
    }

    try {
      const url =
        blogFilter === "all"
          ? `${BASE_URL}blogs/getAllBlogs`
          : `${BASE_URL}blogs/getActiveBlogs`;

      const response = await axios.get(url);
      const blogsArray = response.data.data;
      setAllBlogs(blogsArray || []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  };

  const handleDeleteBlog = async (blogId) => {
    // Check delete permission
    if (!canDelete && !hasFullAccess) {
      alert("You don't have permission to delete blogs");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this blog?")) {
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}blogs/deleteBlogs/${blogId}`
      );

      if (response.data.success) {
        fetchAllBlogs();
      }
    } catch (error) {
      console.error("Error deleting blog:", error);
    }
  };

  const handleToggleStatus = async (blogId, currentStatus) => {
    // Check update permission for status toggle
    if (!canUpdate && !hasFullAccess) {
      alert("You don't have permission to update blog status");
      return;
    }

    setLoadingToggles((prev) => ({ ...prev, [blogId]: true }));

    const newStatus = !currentStatus;

    try {
      const response = await axios.post(`${BASE_URL}blogs/editBlogStatus`, {
        blogId,
        status: newStatus,
      });

      if (response.data.success) {
        setAllBlogs((prevBlogs) =>
          prevBlogs.map((blog) =>
            blog._id === blogId ? { ...blog, isActive: newStatus } : blog
          )
        );
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoadingToggles((prev) => ({ ...prev, [blogId]: false }));
    }
  };

  useEffect(() => {
    fetchAllBlogs();
  }, [blogFilter, canView, hasFullAccess]);

  // If user has no permission to view blogs, show access denied
  if (!canView && !hasFullAccess) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-md p-6 text-center">
          <Typography variant="h4" className="font-bold text-gray-800 mb-4">
            Access Denied
          </Typography>
          <Typography variant="body1" className="text-gray-600">
            You don't have permission to access blog management.
          </Typography>
        </div>
      </div>
    );
  }

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
    "image",
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Blog Form Section - Only show if user has add or update permission */}
      {(canAdd || canUpdate || hasFullAccess) && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <Typography variant="h4" className="font-bold text-gray-800 mb-6">
            {isEditing ? "Edit Blog Post" : "Create New Blog Post"}
          </Typography>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <TextField
                label="Blog Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                inputProps={{ maxLength: 80 }}
                variant="outlined"
                fullWidth
                className="bg-gray-50"
              />

              <TextField
                label="Author Name"
                name="author"
                value={formData.author}
                onChange={handleChange}
                required
                variant="outlined"
                fullWidth
                className="bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Typography
                variant="subtitle1"
                className="font-medium text-gray-700"
              >
                Blog Content
              </Typography>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <QuillEditor
                  value={formData.content}
                  onChange={(value) =>
                    setFormData({ ...formData, content: value })
                  }
                />
              </div>
            </div>

            <TextField
              label="Summary"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              className="bg-gray-50"
            />

            <TextField
              label="Categories (comma separated)"
              name="categories"
              value={formData.categories}
              onChange={handleChange}
              required
              variant="outlined"
              fullWidth
              className="bg-gray-50"
            />

            <div className="space-y-2">
              <Typography
                variant="subtitle1"
                className="font-medium text-gray-700"
              >
                Featured Image
              </Typography>
              <div className="flex items-center space-x-4">
                <Button
                  variant="outlined"
                  component="label"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  startIcon={<Publish />}
                >
                  Upload Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                {imagePreview && (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-24 object-cover rounded-lg border border-gray-200"
                    />
                    <IconButton
                      size="small"
                      className="absolute -top-2 -right-2 bg-white shadow-md hover:bg-gray-100"
                      onClick={() => setImagePreview(null)}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </div>
                )}
              </div>
              <Typography variant="caption" className="text-gray-500">
                Recommended size: 1200x630px (less than 1MB)
              </Typography>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              {isEditing && (
                <Button
                  variant="outlined"
                  color="secondary"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setFormData({
                      title: "",
                      description: "",
                      summary: "",
                      content: "",
                      tags: "",
                      categories: "",
                      author: "",
                      publishDate: "",
                    });
                    setImagePreview(null);
                    setSelectedImageFile(null);
                    setIsEditing(false);
                    setEditingBlogId(null);
                  }}
                >
                  Cancel
                </Button>
              )}

              <Button
                variant="contained"
                color="primary"
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 shadow-sm"
                startIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <Save />
                  )
                }
                disabled={isSubmitting}
              >
                {isEditing ? "Update Blog" : "Publish Blog"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Blog List Section */}
      <div className="bg-white rounded-xl shadow-md p-6 gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <Typography variant="h4" className="font-bold text-gray-800">
            Blog Posts
          </Typography>

          <div className="mt-4 sm:mt-0">
            <ToggleButtonGroup
              value={blogFilter}
              exclusive
              onChange={(e, newValue) => newValue && setBlogFilter(newValue)}
              aria-label="blog filter"
              size="small"
            >
              <ToggleButton value="active" aria-label="active blogs">
                <span
                  className={`text-sm ${
                    blogFilter === "active" ? "font-medium" : ""
                  }`}
                >
                  Active
                </span>
              </ToggleButton>
              <ToggleButton value="all" aria-label="all blogs">
                <span
                  className={`text-sm ${
                    blogFilter === "all" ? "font-medium" : ""
                  }`}
                >
                  All
                </span>
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </div>

        {allBlogs.length === 0 ? (
          <div className="text-center py-12">
            <Typography variant="h6" className="text-gray-500">
              No blog posts found
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3}>
            {allBlogs.map((blog) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={blog._id}>
                <Card className="h-full flex flex-col transition-all duration-200 hover:shadow-lg hover:transform hover:-translate-y-1">
                  <div className="relative">
                    {blog.image && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={blog.image}
                        alt={blog.title}
                        className="cursor-pointer"
                        onClick={() => handleCardClick(blog)}
                      />
                    )}

                    {/* Status Toggle Button - Only show if user has update permission */}
                    {(canUpdate || hasFullAccess) && (
                      <div className="absolute top-2 right-2 z-10">
                        <Tooltip
                          title={blog.isActive ? "Deactivate" : "Activate"}
                          arrow
                        >
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(blog._id, blog.isActive);
                            }}
                            size="small"
                            className={`${
                              blog.isActive
                                ? "bg-green-100 hover:bg-green-200"
                                : "bg-red-100 hover:bg-red-200"
                            } shadow-sm`}
                            disabled={loadingToggles[blog._id]}
                          >
                            {loadingToggles[blog._id] ? (
                              <CircularProgress size={20} />
                            ) : blog.isActive ? (
                              <ToggleOn className="text-green-600" />
                            ) : (
                              <ToggleOff className="text-red-600" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </div>
                    )}
                  </div>

                  <CardContent className="flex-grow flex flex-col">
                    <Typography
                      variant="h6"
                      className="font-bold cursor-pointer hover:text-blue-600 line-clamp-2"
                      onClick={() => handleCardClick(blog)}
                    >
                      {blog.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      className="text-gray-600 mt-2 line-clamp-3"
                    >
                      {blog.summary}
                    </Typography>

                    <div className="mt-3 flex flex-wrap gap-1">
                      {blog.categories?.slice(0, 3).map((category, index) => (
                        <Chip
                          key={index}
                          label={category}
                          size="small"
                          variant="outlined"
                          className="text-xs"
                        />
                      ))}
                      {blog.categories?.length > 3 && (
                        <Chip
                          label={`+${blog.categories.length - 3}`}
                          size="small"
                          variant="outlined"
                          className="text-xs"
                        />
                      )}
                    </div>

                    <div className="mt-auto pt-4 flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <Avatar
                          sx={{ width: 24, height: 24 }}
                          className="bg-blue-500 text-white text-xs"
                        >
                          {blog.author?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Typography variant="caption" className="text-gray-500">
                          {blog.author}
                        </Typography>
                      </div>

                      <div className="flex space-x-1">
                        <Tooltip title="View" arrow>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCardClick(blog);
                            }}
                            className="hover:bg-blue-50"
                          >
                            <Visibility
                              fontSize="small"
                              className="text-blue-500"
                            />
                          </IconButton>
                        </Tooltip>

                        {/* Edit Button - Only show if user has update permission */}
                        {(canUpdate || hasFullAccess) && (
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(blog);
                              }}
                              className="hover:bg-gray-100"
                            >
                              <Edit
                                fontSize="small"
                                className="text-gray-600"
                              />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Delete Button - Only show if user has delete permission */}
                        {(canDelete || hasFullAccess) && (
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteBlog(blog._id);
                              }}
                              className="hover:bg-red-50"
                            >
                              <Delete
                                fontSize="small"
                                className="text-red-500"
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </div>

      {/* Blog Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {selectedBlog && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <Typography variant="h4" className="font-bold text-gray-800">
                {selectedBlog.title}
              </Typography>
              <IconButton
                aria-label="close"
                onClick={() => setOpenDialog(false)}
                sx={{
                  color: "#64748b",
                  "&:hover": {
                    backgroundColor: "#e2e8f0",
                  },
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <div className="max-h-[80vh] overflow-y-auto">
                {selectedBlog.image && (
                  <div className="w-full h-64 md:h-96 bg-gray-100">
                    <img
                      src={selectedBlog.image}
                      alt={selectedBlog.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="p-6 space-y-6">
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Avatar
                        sx={{ width: 32, height: 32 }}
                        className="bg-blue-500 text-white"
                      >
                        {selectedBlog.author?.charAt(0)?.toUpperCase()}
                      </Avatar>
                      <span>{selectedBlog.author}</span>
                    </div>

                    {selectedBlog.createdAt && (
                      <div className="flex items-center">
                        <span className="text-gray-400 mx-2">•</span>
                        <span>
                          {format(
                            new Date(selectedBlog.createdAt),
                            "MMM d, yyyy"
                          )}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center">
                      <span className="text-gray-400 mx-2">•</span>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedBlog.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {selectedBlog.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {selectedBlog.categories?.map((category, index) => (
                      <Chip
                        key={index}
                        label={category}
                        variant="outlined"
                        className="border-gray-300 text-gray-700"
                      />
                    ))}
                  </div>

                  <Divider />

                  <Typography variant="h6" className="font-bold text-gray-800">
                    Summary
                  </Typography>
                  <Typography variant="body1" className="text-gray-700">
                    {selectedBlog.summary}
                  </Typography>

                  <Divider />

                  <Typography variant="h6" className="font-bold text-gray-800">
                    Content
                  </Typography>
                  <div
                    className="prose max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                  />
                </div>
              </div>
            </DialogContent>
          </>
        )}
      </Dialog>
    </div>
  );
};

export default CreateBlog;
